import { t } from "@/lib/i18n/interpolate";

export type EssayType =
  | "argumentative"
  | "expository"
  | "compare-contrast"
  | "narrative"
  | "research"
  | "persuasive";

export interface OutlineSection {
  heading: string;
  items: string[];
}

/**
 * Every reader-visible outline step is one translatable string in the widget's
 * `*_STRINGS`, addressed by these keys. A step is a whole sentence with `{topic}` and
 * `{number}` slots rather than fragments joined in code, so a language can put the
 * topic or the number wherever its grammar wants them. Only the section letters and
 * roman numerals, which are formatting, are produced here.
 */
export type OutlineStepKey =
  | "introArgumentativeHook"
  | "introArgumentativeBackground"
  | "introArgumentativeOpposing"
  | "introArgumentativeThesis"
  | "introExpositoryHook"
  | "introExpositoryBackground"
  | "introExpositoryTerms"
  | "introExpositoryThesis"
  | "introCompareHook"
  | "introCompareBackground"
  | "introCompareMeaningful"
  | "introCompareThesis"
  | "introNarrativeHook"
  | "introNarrativeSetting"
  | "introNarrativeCharacters"
  | "introNarrativePurpose"
  | "introResearchHook"
  | "introResearchBackground"
  | "introResearchQuestion"
  | "introResearchThesis"
  | "introPersuasiveHook"
  | "introPersuasiveBackground"
  | "introPersuasiveCredibility"
  | "introPersuasiveThesis"
  | "bodyArgumentativeTopic"
  | "bodyArgumentativeEvidence"
  | "bodyArgumentativeAnalysis"
  | "bodyArgumentativeCounter"
  | "bodyArgumentativeTransitionNext"
  | "bodyArgumentativeTransitionLast"
  | "bodyExpositoryTopic"
  | "bodyExpositoryExplanation"
  | "bodyExpositoryExample"
  | "bodyExpositoryAnalysis"
  | "bodyExpositoryTransitionNext"
  | "bodyExpositoryTransitionLast"
  | "bodyCompareFirstTopic"
  | "bodyCompareFirstSubject1"
  | "bodyCompareFirstSubject2"
  | "bodyCompareFirstAnalysis"
  | "bodyCompareFirstTransition"
  | "bodyCompareTopic"
  | "bodyCompareSubject1"
  | "bodyCompareSubject2"
  | "bodyCompareAnalysis"
  | "bodyCompareTransitionNext"
  | "bodyCompareTransitionLast"
  | "bodyNarrativeFirstTopic"
  | "bodyNarrativeFirstEvents"
  | "bodyNarrativeFirstDetails"
  | "bodyNarrativeFirstCharacter"
  | "bodyNarrativeFirstTransition"
  | "bodyNarrativeLastTopic"
  | "bodyNarrativeLastResolved"
  | "bodyNarrativeLastAftermath"
  | "bodyNarrativeLastReflect"
  | "bodyNarrativeLastTransition"
  | "bodyNarrativeMiddleTopic"
  | "bodyNarrativeMiddleEvents"
  | "bodyNarrativeMiddleDialogue"
  | "bodyNarrativeMiddleTension"
  | "bodyNarrativeMiddleTransition"
  | "bodyResearchTopic"
  | "bodyResearchEvidence"
  | "bodyResearchAnalysis"
  | "bodyResearchDiscussion"
  | "bodyResearchTransitionNext"
  | "bodyResearchTransitionLast"
  | "bodyPersuasiveTopic"
  | "bodyPersuasiveEvidence"
  | "bodyPersuasiveEmotional"
  | "bodyPersuasiveRefutation"
  | "bodyPersuasiveTransitionNext"
  | "bodyPersuasiveTransitionLast"
  | "concArgumentativeRestate"
  | "concArgumentativeSummarize"
  | "concArgumentativeImplications"
  | "concArgumentativeClosing"
  | "concExpositoryRestate"
  | "concExpositoryRecap"
  | "concExpositorySignificance"
  | "concExpositoryClosing"
  | "concCompareRestate"
  | "concCompareSummarize"
  | "concCompareEvaluation"
  | "concCompareClosing"
  | "concNarrativeReflect"
  | "concNarrativeConnect"
  | "concNarrativeResolution"
  | "concNarrativeClosing"
  | "concResearchRestate"
  | "concResearchSynthesize"
  | "concResearchLimitations"
  | "concResearchFuture"
  | "concResearchClosing"
  | "concPersuasiveRestate"
  | "concPersuasiveSummarize"
  | "concPersuasiveEmotional"
  | "concPersuasiveCall";

/** The step strings an outline is built from: the widget's strings carry all of them. */
export type OutlineStepStrings = Readonly<Record<OutlineStepKey, string>>;

/** The section labels the outline headings are built from. */
export interface OutlineHeadingStrings {
  bodyParagraph: string;
  conclusion: string;
  introduction: string;
  yourTopicPlaceholder: string;
}

const ROMAN_NUMERALS = ["I", "II", "III", "IV", "V", "VI", "VII"];
const ITEM_LETTERS = ["A", "B", "C", "D", "E"];

export function romanNumeral(n: number): string {
  return ROMAN_NUMERALS[n - 1] || String(n);
}

const INTRO_KEYS: Record<EssayType, readonly OutlineStepKey[]> = {
  argumentative: [
    "introArgumentativeHook",
    "introArgumentativeBackground",
    "introArgumentativeOpposing",
    "introArgumentativeThesis",
  ],
  expository: [
    "introExpositoryHook",
    "introExpositoryBackground",
    "introExpositoryTerms",
    "introExpositoryThesis",
  ],
  "compare-contrast": [
    "introCompareHook",
    "introCompareBackground",
    "introCompareMeaningful",
    "introCompareThesis",
  ],
  narrative: [
    "introNarrativeHook",
    "introNarrativeSetting",
    "introNarrativeCharacters",
    "introNarrativePurpose",
  ],
  research: [
    "introResearchHook",
    "introResearchBackground",
    "introResearchQuestion",
    "introResearchThesis",
  ],
  persuasive: [
    "introPersuasiveHook",
    "introPersuasiveBackground",
    "introPersuasiveCredibility",
    "introPersuasiveThesis",
  ],
};

const CONCLUSION_KEYS: Record<EssayType, readonly OutlineStepKey[]> = {
  argumentative: [
    "concArgumentativeRestate",
    "concArgumentativeSummarize",
    "concArgumentativeImplications",
    "concArgumentativeClosing",
  ],
  expository: [
    "concExpositoryRestate",
    "concExpositoryRecap",
    "concExpositorySignificance",
    "concExpositoryClosing",
  ],
  "compare-contrast": [
    "concCompareRestate",
    "concCompareSummarize",
    "concCompareEvaluation",
    "concCompareClosing",
  ],
  narrative: [
    "concNarrativeReflect",
    "concNarrativeConnect",
    "concNarrativeResolution",
    "concNarrativeClosing",
  ],
  research: [
    "concResearchRestate",
    "concResearchSynthesize",
    "concResearchLimitations",
    "concResearchFuture",
    "concResearchClosing",
  ],
  persuasive: [
    "concPersuasiveRestate",
    "concPersuasiveSummarize",
    "concPersuasiveEmotional",
    "concPersuasiveCall",
  ],
};

/** Body paragraphs that differ only in the closing transition (last vs. not last). */
interface TransitionBody {
  lead: readonly OutlineStepKey[];
  transitionLast: OutlineStepKey;
  transitionNext: OutlineStepKey;
}

const TRANSITION_BODY_KEYS: Partial<Record<EssayType, TransitionBody>> = {
  argumentative: {
    lead: [
      "bodyArgumentativeTopic",
      "bodyArgumentativeEvidence",
      "bodyArgumentativeAnalysis",
      "bodyArgumentativeCounter",
    ],
    transitionNext: "bodyArgumentativeTransitionNext",
    transitionLast: "bodyArgumentativeTransitionLast",
  },
  expository: {
    lead: [
      "bodyExpositoryTopic",
      "bodyExpositoryExplanation",
      "bodyExpositoryExample",
      "bodyExpositoryAnalysis",
    ],
    transitionNext: "bodyExpositoryTransitionNext",
    transitionLast: "bodyExpositoryTransitionLast",
  },
  research: {
    lead: [
      "bodyResearchTopic",
      "bodyResearchEvidence",
      "bodyResearchAnalysis",
      "bodyResearchDiscussion",
    ],
    transitionNext: "bodyResearchTransitionNext",
    transitionLast: "bodyResearchTransitionLast",
  },
  persuasive: {
    lead: [
      "bodyPersuasiveTopic",
      "bodyPersuasiveEvidence",
      "bodyPersuasiveEmotional",
      "bodyPersuasiveRefutation",
    ],
    transitionNext: "bodyPersuasiveTransitionNext",
    transitionLast: "bodyPersuasiveTransitionLast",
  },
};

const COMPARE_FIRST_KEYS: readonly OutlineStepKey[] = [
  "bodyCompareFirstTopic",
  "bodyCompareFirstSubject1",
  "bodyCompareFirstSubject2",
  "bodyCompareFirstAnalysis",
  "bodyCompareFirstTransition",
];

const NARRATIVE_FIRST_KEYS: readonly OutlineStepKey[] = [
  "bodyNarrativeFirstTopic",
  "bodyNarrativeFirstEvents",
  "bodyNarrativeFirstDetails",
  "bodyNarrativeFirstCharacter",
  "bodyNarrativeFirstTransition",
];

const NARRATIVE_LAST_KEYS: readonly OutlineStepKey[] = [
  "bodyNarrativeLastTopic",
  "bodyNarrativeLastResolved",
  "bodyNarrativeLastAftermath",
  "bodyNarrativeLastReflect",
  "bodyNarrativeLastTransition",
];

const NARRATIVE_MIDDLE_KEYS: readonly OutlineStepKey[] = [
  "bodyNarrativeMiddleTopic",
  "bodyNarrativeMiddleEvents",
  "bodyNarrativeMiddleDialogue",
  "bodyNarrativeMiddleTension",
  "bodyNarrativeMiddleTransition",
];

function compareBodyKeys(index: number, bodyCount: number): OutlineStepKey[] {
  if (index === 0) {
    return [...COMPARE_FIRST_KEYS];
  }
  return [
    "bodyCompareTopic",
    "bodyCompareSubject1",
    "bodyCompareSubject2",
    "bodyCompareAnalysis",
    index < bodyCount - 1
      ? "bodyCompareTransitionNext"
      : "bodyCompareTransitionLast",
  ];
}

function narrativeBodyKeys(index: number, bodyCount: number): OutlineStepKey[] {
  if (index === 0) {
    return [...NARRATIVE_FIRST_KEYS];
  }
  if (index === bodyCount - 1) {
    return [...NARRATIVE_LAST_KEYS];
  }
  return [...NARRATIVE_MIDDLE_KEYS];
}

function bodyKeys(
  type: EssayType,
  index: number,
  bodyCount: number
): OutlineStepKey[] {
  if (type === "compare-contrast") {
    return compareBodyKeys(index, bodyCount);
  }
  if (type === "narrative") {
    return narrativeBodyKeys(index, bodyCount);
  }
  const body = TRANSITION_BODY_KEYS[type];
  if (!body) {
    return [];
  }
  const isLast = index >= bodyCount - 1;
  return [...body.lead, isLast ? body.transitionLast : body.transitionNext];
}

function renderItems(
  keys: readonly OutlineStepKey[],
  strings: OutlineStepStrings,
  values: Record<string, string | number>
): string[] {
  return keys.map((key, i) => `${ITEM_LETTERS[i]}. ${t(strings[key], values)}`);
}

/** Builds the introduction, body and conclusion sections of an essay outline. */
export function generateOutline(
  type: EssayType,
  topic: string,
  bodyCount: number,
  strings: OutlineStepStrings & OutlineHeadingStrings
): OutlineSection[] {
  const topicLabel = topic || strings.yourTopicPlaceholder;
  const sections: OutlineSection[] = [
    {
      heading: `${romanNumeral(1)}. ${strings.introduction}`,
      items: renderItems(INTRO_KEYS[type], strings, { topic: topicLabel }),
    },
  ];

  for (let i = 0; i < bodyCount; i++) {
    sections.push({
      heading: `${romanNumeral(i + 2)}. ${strings.bodyParagraph} ${i + 1}`,
      items: renderItems(bodyKeys(type, i, bodyCount), strings, {
        number: i + 1,
      }),
    });
  }

  sections.push({
    heading: `${romanNumeral(bodyCount + 2)}. ${strings.conclusion}`,
    items: renderItems(CONCLUSION_KEYS[type], strings, {}),
  });

  return sections;
}
